import { useQuery } from "@tanstack/react-query";
import { useState, useTransition } from "react";
import { useFormatter, useTranslations } from "use-intl";
import { Bot, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/components/app/copy-button";
import { PendingButton } from "@/components/app/pending-button";
import { Section } from "@/components/app/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, call } from "@/lib/api";

const TOKENS_KEY = ["mcp-tokens"] as const;

function Snippet({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <CopyButton value={value} />
      </div>
      <pre className="overflow-x-auto rounded-xl bg-muted p-3 text-[11px] leading-relaxed whitespace-pre-wrap break-all">
        {value}
      </pre>
    </div>
  );
}

/** Profile section: MCP server address and personal access tokens. */
export function McpSection() {
  const t = useTranslations("mcp");
  const tc = useTranslations("common");
  const format = useFormatter();
  const tokens = useQuery({ queryKey: TOKENS_KEY, queryFn: () => call(api["mcp-tokens"].$get()) });
  const [name, setName] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const [pending, start] = useTransition();
  const url = `${window.location.origin}/api/mcp`;

  const create = () =>
    start(async () => {
      try {
        const res = await call(api["mcp-tokens"].$post({ json: { name: name.trim() } }));
        setCreated(res.token);
        setName("");
        await tokens.refetch();
      } catch {
        toast.error(tc("error"));
      }
    });

  const revoke = (id: string) =>
    start(async () => {
      await call(api["mcp-tokens"][":id"].$delete({ param: { id } }));
      await tokens.refetch();
    });

  return (
    <Section title={t("title")}>
      <Card className="py-4">
        <CardContent className="space-y-4 px-4">
          <p className="flex gap-2 text-sm text-muted-foreground">
            <Bot className="mt-0.5 size-4 shrink-0 text-primary" /> {t("intro")}
          </p>
          <div className="grid gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">{t("serverUrl")}</span>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-lg bg-muted px-2.5 py-2 text-xs">{url}</code>
              <CopyButton value={url} />
            </div>
          </div>

          <div className="grid gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t("tokens")}</span>
            <ul className="divide-y rounded-xl border">
              {(tokens.data ?? []).map((tok) => (
                <li key={tok.id} className="flex items-center gap-3 px-3 py-2.5">
                  <KeyRound className="size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{tok.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      <code>{tok.prefix}…</code> ·{" "}
                      {tok.lastUsedAt
                        ? t("lastUsed", { date: format.relativeTime(new Date(tok.lastUsedAt), new Date()) })
                        : t("neverUsed")}
                    </p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label={t("revoke")}>
                        <Trash2 />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("revokeTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("revokeText")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{tc("cancel")}</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={() => revoke(tok.id)}>
                          {t("revoke")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </li>
              ))}
              {tokens.data?.length === 0 && <li className="px-3 py-3 text-sm text-muted-foreground">{t("noTokens")}</li>}
            </ul>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) create();
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("tokenName")} maxLength={60} className="h-10 text-base" />
              <PendingButton type="submit" className="h-10" pending={pending} disabled={!name.trim()}>
                {t("createToken")}
              </PendingButton>
            </form>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!created} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("newTokenTitle")}</DialogTitle>
            <DialogDescription>{t("newTokenText")}</DialogDescription>
          </DialogHeader>
          {created && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all rounded-lg bg-muted px-2.5 py-2 text-xs">{created}</code>
                <CopyButton value={created} />
              </div>
              <Snippet
                label={t("claudeCode")}
                value={`claude mcp add --transport http liftquest ${url} --header "Authorization: Bearer ${created}"`}
              />
              <Snippet
                label={t("otherClients")}
                value={JSON.stringify(
                  { mcpServers: { liftquest: { type: "http", url, headers: { Authorization: `Bearer ${created}` } } } },
                  null,
                  2,
                )}
              />
              <p className="text-xs text-muted-foreground">⚠️ {t("warning")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Section>
  );
}
